import { DownloadOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { DeleteOutlined, FileOutlined, PdfOutlined } from '@vindr/icons';
import { numberToTimeCounter } from '@vindr/utils/converter';
import { Progress } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FileName from '../../../../ui/FIleName/FileName';
import { statusMap } from '../../../Upload/context/UploadAction';
import { isFullScreen, requestFullscreen } from '../../Webcam/helper/fullscreen';
import { CaptureManagerState, Processing } from '../context/type';
import './MediaCardView.css';

async function captureThumbnailVideo(videoURL) {
  // const videoBlob = await fetch(blobURL).then((res) => res.blob());
  // const videoURL = URL.createObjectURL(videoBlob);
  const video = document.createElement('video');

  video.crossOrigin = 'anonymous';
  video.src = videoURL;
  video.autoplay = true;
  video.preload = 'metadata';
  // Load video in Safari / IE11
  video.muted = true;
  video.playsInline = true;

  const canvas = document.createElement('canvas');
  let thumbnail = '';
  try {
    await video.play();
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');

    context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
    const thumbnailBlob = (await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg')
    )) as Blob;
    thumbnail = URL.createObjectURL(thumbnailBlob);
  } catch (e) {
    await new Promise<void>((resolve) => {
      video.onloadedmetadata = () => {
        video.currentTime = 1; //? seek to 1 second to ensure enough video data for capture

        video.onseeked = () => {
          const context = canvas.getContext('2d');

          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;

          context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight); //? draw the video frame to canvas
          video.pause();

          resolve();
        };
      };
    });
    thumbnail = canvas.toDataURL('image/png');
  }

  const duration = video.duration;

  // URL.revokeObjectURL(videoURL); // Destroy the video once we are done with it.

  // Destroy the video once we are done with it.
  video.pause(); // pause the video before removing it
  video.src = ''; // empty the video source to release any resources associated with it
  video.load(); // force the browser to release resources used by the video element
  canvas.remove();
  video.remove();
  // Destroy the canvas once we are done with it.
  canvas.remove();

  return { thumbnail, duration };
}

// const placeholder = 'assets/images/empty.svg';
const placeholder = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

const MediaCardView = ({
  tag,
  url,
  name,
  process,
  onDelete,
  onDownload,
}: {
  tag: CaptureManagerState['storeFiles'][number]['tag'];
  url: CaptureManagerState['storeFiles'][number]['url'];
  name: CaptureManagerState['storeFiles'][number]['name'];
  process: Processing;
  onDelete: () => void;
  onDownload: () => void;
}) => {
  const [thumbnail, setThumbnail] = useState(placeholder);
  const [duration, setDuration] = useState<number>(null);
  const { t } = useTranslation('Vindoc');

  useEffect(() => {
    if (tag === 'video') {
      captureThumbnailVideo(url).then(({ thumbnail, duration }) => {
        setThumbnail(thumbnail);
        setDuration(duration);
      });
    }
  }, [tag, url]);

  const openVideo = useCallback(() => {
    const video = document.createElement('video');

    video.crossOrigin = 'anonymous';
    video.src = url;
    video.controls = true;
    video.autoplay = true;
    // video.loop = true;
    // video.muted = true;
    video.classList.add('video-preview');

    video.setAttribute('controlsList', 'noremoteplayback');

    video.style.cssText = `
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      z-index: -1;
      background-color: black;
    `;

    const closeVideo = () => {
      if (!isFullScreen()) {
        document.body.removeChild(video);
      }
    };
    document.body.appendChild(video);
    requestFullscreen(video);

    //? setTimeout for not close fullscreen immediately
    setTimeout(() => {
      video.addEventListener('fullscreenchange', closeVideo);
      video.addEventListener('mozfullscreenchange', closeVideo);
      video.addEventListener('MSFullscreenChange', closeVideo);
      video.addEventListener('webkitfullscreenchange', closeVideo);
    }, 500);
  }, [url]);

  return (
    <div
      className="media-card-view mb-auto flex h-full w-full flex-col items-center justify-between"
      style={{
        boxShadow: '0 1px 1px rgba(9, 30, 66, 0.31), 0 0 1px 0 rgba(9, 30, 66, 0.31)',
      }}
    >
      <div className="preview relative flex h-0 w-full flex-1 items-center bg-[#323940]">
        {process?.status !== 'success' && process?.progress !== 100 && (
          <div className="pointer-events-none absolute top-0 left-0 h-full w-full bg-black opacity-30" />
        )}
        {(process?.status === 'converting' && process?.progress < 100) ||
        ['success', 'uploading', 'exception'].includes(process?.status) ? (
          <div className="circle absolute top-[4px] left-[4px] h-4 w-4">
            <Progress
              type="circle"
              status={statusMap(process?.status)}
              percent={process?.progress | 0}
              width={25}
              {...(statusMap(process?.status) === 'converting' && { strokeColor: '#135f63' })}
            />
          </div>
        ) : null}

        <article className="action-backdrop pointer-events-none absolute top-0 right-0 h-full w-full bg-[#0000004d]">
          <div className="action-wrapper pointer-events-auto z-[1] flex items-center justify-end p-1 text-[20px] text-[#fafafa]">
            <DownloadOutlined
              onClick={onDownload}
              className="action-icon bg-[#172126] p-1"
              title={t('Download')}
            />
            <DeleteOutlined
              onClick={onDelete}
              title={t('Delete')}
              className="action-icon ml-1 bg-[#172126] p-1 hover:text-[#dc4446]"
            />
          </div>
        </article>

        {['img', 'video'].includes(tag) && (
          <>
            <img
              src={tag === 'video' ? thumbnail : url}
              alt="media"
              className="h-auto w-auto object-cover"
              onError={(e) => {
                e.target['onerror'] = null;
                e.target['src'] = placeholder;
              }}
            />
            {tag === 'video' && (
              <>
                <PlayCircleOutlined
                  onClick={openVideo}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform cursor-pointer text-6xl text-white"
                />
                <p className="absolute bottom-[4px] right-[4px] m-0 text-xs text-white">
                  {!!duration && numberToTimeCounter(duration)}
                </p>
              </>
            )}
          </>
        )}
        {tag === 'pdf' && <PdfOutlined className="flex-1 justify-center text-7xl text-[#ff5630]" />}
        {tag === 'other' && (
          <FileOutlined className="flex-1 justify-center text-7xl text-[#758195]" />
        )}
      </div>
      <div className="title-box z-[1] w-full bg-[#1d2125] p-2">
        <FileName name={name} />
      </div>
    </div>
  );
};

export default MediaCardView;
